import { api } from './apiService';
import type { MiningConfigs, MiningGroup } from '@/types/mining';

interface ApiMiningGroup {
  id: string;
  user_id: string;
  token_id: string;
  power_consumption_wh?: number | null;
  mining_duration_days?: number | null;
  total_kwh_direct?: number | null;
  description?: string | null;
  created_at: string;
  updated_at: string;
}

function rowToGroup(row: ApiMiningGroup): MiningGroup & { tokenId: string } {
  return {
    id: row.id,
    tokenId: row.token_id,
    powerConsumptionWh: row.power_consumption_wh != null ? Number(row.power_consumption_wh) : undefined,
    miningDurationDays: row.mining_duration_days != null ? Number(row.mining_duration_days) : undefined,
    totalKwhDirect: row.total_kwh_direct != null ? Number(row.total_kwh_direct) : undefined,
    description: row.description ?? undefined,
  };
}

export const miningApiService = {
  async listAsConfigs(): Promise<MiningConfigs> {
    const { data } = await api.get<ApiMiningGroup[]>('/mining-groups');
    const configs: MiningConfigs = {};
    for (const row of data) {
      const group = rowToGroup(row);
      const { tokenId, ...groupData } = group;
      if (!configs[tokenId]) configs[tokenId] = [];
      configs[tokenId].push(groupData);
    }
    return configs;
  },

  async create(tokenId: string, group: Omit<MiningGroup, 'id'>): Promise<MiningGroup> {
    const { data } = await api.post<ApiMiningGroup>('/mining-groups', {
      token_id: tokenId,
      power_consumption_wh: group.powerConsumptionWh,
      mining_duration_days: group.miningDurationDays,
      total_kwh_direct: group.totalKwhDirect,
      description: group.description,
    });
    const { tokenId: _tid, ...result } = rowToGroup(data);
    return result;
  },

  async update(id: string, tokenId: string, group: Partial<Omit<MiningGroup, 'id'>>): Promise<MiningGroup> {
    const { data } = await api.put<ApiMiningGroup>(`/mining-groups/${id}`, {
      token_id: tokenId,
      power_consumption_wh: group.powerConsumptionWh,
      mining_duration_days: group.miningDurationDays,
      total_kwh_direct: group.totalKwhDirect,
      description: group.description,
    });
    const { tokenId: _tid, ...result } = rowToGroup(data);
    return result;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/mining-groups/${id}`);
  },
};
